import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { config } from "../../config";

interface Organization {
  id: string;
  name: string;
  slug: string;
  subdomain?: string;
  createdAt: string;
  updatedAt: string;
  settings: {
    ssoOnly: boolean;
    allowGoogle: boolean;
    allowMicrosoft: boolean;
    allowPassword: boolean;
    mfaRequired: boolean;
  };
}

interface Domain {
  domain: string;
  organizationId: string;
  verified: boolean;
  hasSubdomain: boolean;
  subdomain?: string;
}

interface Member {
  organizationId: string;
  userId: string;
  role: string;
  permissions: string[];
  joinedAt: string;
}

type Tab = "details" | "members" | "domains";

export function OrganizationDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const [org, setOrg] = useState<Organization | null>(null);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("details");

  // Domain add form
  const [newDomain, setNewDomain] = useState("");
  const [domainError, setDomainError] = useState<string | null>(null);
  const [isAddingDomain, setIsAddingDomain] = useState(false);

  // Member add form
  const [newMemberUserId, setNewMemberUserId] = useState("");
  const [newMemberRole, setNewMemberRole] = useState("member");
  const [memberError, setMemberError] = useState<string | null>(null);
  const [isAddingMember, setIsAddingMember] = useState(false);

  const fetchOrganization = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${config.apiBaseUrl}/v1/admin/organizations/${id}`, {
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to fetch organization");

      const data = await response.json();
      setOrg(data.organization);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const fetchDomains = useCallback(async () => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/v1/admin/organizations/${id}/domains`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setDomains(data.domains ?? []);
      }
    } catch {
      // Non-critical
    }
  }, [id]);

  const fetchMembers = useCallback(async () => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/v1/admin/organizations/${id}/members`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setMembers(data.members ?? []);
      }
    } catch {
      // Non-critical
    }
  }, [id]);

  useEffect(() => {
    fetchOrganization();
    fetchDomains();
    fetchMembers();
  }, [fetchOrganization, fetchDomains, fetchMembers]);

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return;
    setDomainError(null);
    setIsAddingDomain(true);

    try {
      const response = await fetch(`${config.apiBaseUrl}/v1/admin/organizations/${id}/domains`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain: newDomain.trim() }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error ?? "Failed to add domain");
      }

      setNewDomain("");
      fetchDomains();
    } catch (err) {
      setDomainError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsAddingDomain(false);
    }
  };

  const handleRemoveDomain = async (domain: string) => {
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/admin/organizations/${id}/domains/${domain}`,
        { method: "DELETE", credentials: "include" },
      );
      if (response.ok) {
        fetchDomains();
      }
    } catch {
      // Handle silently
    }
  };

  const handleVerifyDomain = async (domain: string) => {
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/admin/organizations/${id}/domains/${domain}/verify`,
        { method: "POST", credentials: "include" },
      );
      const data = await response.json();
      if (data.verified) {
        fetchDomains();
      } else {
        setDomainError(data.message);
      }
    } catch {
      setDomainError("Verification failed");
    }
  };

  const handleAddMember = async () => {
    if (!newMemberUserId.trim()) return;
    setMemberError(null);
    setIsAddingMember(true);

    try {
      const response = await fetch(`${config.apiBaseUrl}/v1/admin/organizations/${id}/members`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: newMemberUserId.trim(), role: newMemberRole }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error ?? "Failed to add member");
      }

      setNewMemberUserId("");
      fetchMembers();
    } catch (err) {
      setMemberError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsAddingMember(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/admin/organizations/${id}/members/${userId}`,
        { method: "DELETE", credentials: "include" },
      );
      if (response.ok) {
        fetchMembers();
      }
    } catch {
      // Handle silently
    }
  };

  const handleUpdateMemberRole = async (userId: string, role: string) => {
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/admin/organizations/${id}/members/${userId}`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ role }),
        },
      );
      if (response.ok) {
        fetchMembers();
      }
    } catch {
      // Handle silently
    }
  };

  if (isLoading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">
          <div className="loading-spinner" />
          <p>Loading organization...</p>
        </div>
      </div>
    );
  }

  if (error || !org) {
    return (
      <div className="admin-page">
        <div className="admin-error">
          <p>{error ?? "Organization not found"}</p>
          <Link to="/admin/organizations">Back to Organizations</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <div>
          <Link to="/admin/organizations" className="btn-link">
            &larr; Organizations
          </Link>
          <h1>{org.name}</h1>
          <p className="text-muted">
            {org.slug} {org.subdomain && `| ${org.subdomain}`}
          </p>
        </div>
        <Link to={`/admin/organizations/${id}/edit`} className="btn btn-secondary">
          Edit Organization
        </Link>
      </header>

      <div className="admin-tabs">
        <button
          type="button"
          className={`tab ${activeTab === "details" ? "active" : ""}`}
          onClick={() => setActiveTab("details")}
        >
          Details
        </button>
        <button
          type="button"
          className={`tab ${activeTab === "members" ? "active" : ""}`}
          onClick={() => setActiveTab("members")}
        >
          Members ({members.length})
        </button>
        <button
          type="button"
          className={`tab ${activeTab === "domains" ? "active" : ""}`}
          onClick={() => setActiveTab("domains")}
        >
          Domains ({domains.length})
        </button>
      </div>

      {activeTab === "details" && (
        <div className="admin-card">
          <h2>Organization Details</h2>
          <dl className="detail-list">
            <dt>ID</dt>
            <dd>{org.id}</dd>
            <dt>Name</dt>
            <dd>{org.name}</dd>
            <dt>Slug</dt>
            <dd>{org.slug}</dd>
            <dt>Subdomain</dt>
            <dd>{org.subdomain ?? "None"}</dd>
            <dt>Created</dt>
            <dd>{new Date(org.createdAt).toLocaleString()}</dd>
            <dt>Updated</dt>
            <dd>{new Date(org.updatedAt).toLocaleString()}</dd>
          </dl>

          <h3>Authentication Settings</h3>
          <div className="auth-badges">
            {org.settings.allowPassword && <span className="badge">Password</span>}
            {org.settings.allowGoogle && <span className="badge">Google</span>}
            {org.settings.allowMicrosoft && <span className="badge">Microsoft</span>}
            {org.settings.ssoOnly && <span className="badge badge-warning">SSO Only</span>}
            {org.settings.mfaRequired && <span className="badge badge-info">MFA Required</span>}
          </div>
        </div>
      )}

      {activeTab === "members" && (
        <div className="admin-card">
          <h2>Members</h2>

          <div className="inline-form">
            <input
              type="text"
              placeholder="User ID"
              value={newMemberUserId}
              onChange={(e) => setNewMemberUserId(e.target.value)}
            />
            <select value={newMemberRole} onChange={(e) => setNewMemberRole(e.target.value)}>
              <option value="viewer">Viewer</option>
              <option value="member">Member</option>
              <option value="admin">Admin</option>
              <option value="owner">Owner</option>
            </select>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAddMember}
              disabled={isAddingMember || !newMemberUserId.trim()}
            >
              {isAddingMember ? "Adding..." : "Add Member"}
            </button>
          </div>

          {memberError && <div className="form-error">{memberError}</div>}

          {members.length === 0 ? (
            <p className="text-muted">No members found.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Role</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.userId}>
                    <td>
                      <Link to={`/admin/users/${member.userId}`}>{member.userId}</Link>
                    </td>
                    <td>
                      <select
                        value={member.role}
                        onChange={(e) => handleUpdateMemberRole(member.userId, e.target.value)}
                      >
                        <option value="viewer">Viewer</option>
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                        <option value="owner">Owner</option>
                      </select>
                    </td>
                    <td>{new Date(member.joinedAt).toLocaleDateString()}</td>
                    <td>
                      <button
                        type="button"
                        className="btn-link btn-danger"
                        onClick={() => handleRemoveMember(member.userId)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === "domains" && (
        <div className="admin-card">
          <h2>Email Domains</h2>

          <div className="inline-form">
            <input
              type="text"
              placeholder="example.com"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAddDomain}
              disabled={isAddingDomain || !newDomain.trim()}
            >
              {isAddingDomain ? "Adding..." : "Add Domain"}
            </button>
          </div>

          {domainError && <div className="form-error">{domainError}</div>}

          {domains.length === 0 ? (
            <p className="text-muted">No domains mapped.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {domains.map((d) => (
                  <tr key={d.domain}>
                    <td>{d.domain}</td>
                    <td>
                      {d.verified ? (
                        <span className="badge badge-success">Verified</span>
                      ) : (
                        <span className="badge badge-warning">Unverified</span>
                      )}
                    </td>
                    <td>
                      {!d.verified && (
                        <button
                          type="button"
                          className="btn-link"
                          onClick={() => handleVerifyDomain(d.domain)}
                        >
                          Verify
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn-link btn-danger"
                        onClick={() => handleRemoveDomain(d.domain)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
